import { useRef, useState, useEffect, useMemo, memo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { useVirtualizer } from "@tanstack/react-virtual"
import ChevronRightIcon from "@/shared/icons/totodoicons/stroke/chevron-right.svg?react"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle"
import { useGlobalBadgeCount } from "@/shared/hooks/useGlobalBadgeCount"
import { cn } from "@/shared/utils/general/cn"
import { useMainScroll } from "@/app/providers/MainScrollProvider"
import { useViewContext } from "@/app/stores/viewContext"
import { useSettingBoolean } from "@/features/settings/hooks/useSetting"
import type { RegisteredIconType } from "@/shared/icons/iconRegistry"
import type { ViewItem, ViewData } from "../hooks/useViewData"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { EmptyState } from "./EmptyState"
import { useTaskActions } from "@/features/tasks/hooks/useTaskActions"
import { useActiveTask } from "@/features/task-detail/hooks/useActiveTask"
import { useStaleTaskDetection } from "@/features/task-detail/hooks/useStaleTaskDetection"
import { DndTaskList } from "@/features/dnd/DndTaskList"
import { QuickAdd } from "@/features/tasks/components/QuickAdd"
import { db, ObjectType, ObjectStatus } from "@/db/schema"
import { GroupedTaskList, viewShowsGrouping } from "./GroupedTaskList"
import { DateGroupedList } from "./DateGroupedList"
import { computeGroupPositions } from "../utils/groupPositions"
import {
  useTagFilterStore,
  viewShowsTagFilter,
  filterBySelectedTags,
  filterByText,
  getUniqueTagsFromItems,
} from "@/features/tags"
import {
  useAncestorTagStore,
  getInheritedTags,
} from "@/features/tags/ancestorTagStore"
import {
  getCachedViewTags,
  cacheEnrichedViewTags,
} from "../loadingPlaceholders"
import { useShallow } from "zustand/shallow"
import {
  useViewFilterStore,
  applyViewFilters,
  applySortOverride,
  type ViewFilterCriteria,
} from "../stores/viewFilterStore"
import { ViewHeader, type ViewHeaderAction } from "./ViewHeader"
import { LaterProjectsDndList } from "./LaterProjectsDndList"
import { LoggedItemsToggle } from "./LoggedItemsToggle"
import { NewTasksBanner, isNewTask } from "./NewTasksBanner"
import { PinnedSection, usePinnedIds } from "@/features/focus/PinnedSection"
import { useViewSeen } from "../hooks/useViewSeen"
import { useViewMenuStore } from "../stores/viewMenuStore"
import type { ContainerLoggedState } from "../hooks/useContainerLoggedItems"

// ---------------------------------------------------------------------------
// Date group mode helper
// ---------------------------------------------------------------------------

type DateGroupMode = "upcoming" | "logbook" | "trash"

/** Returns the date-group mode for the view, or null if not date-grouped. */
function getDateGroupMode(viewId: string): DateGroupMode | null {
  switch (viewId) {
    case "upcoming":
      return "upcoming"
    case "logbook":
      return "logbook"
    case "trash":
      return "trash"
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** ViewItem enriched with inherited + own tags for tag filtering */
export interface EnrichedViewItem extends ViewItem {
  allTags: string[]
}

interface ViewShellProps {
  /** View title */
  title: string
  /** Icon type from registry (null = no icon) */
  icon?: RegisteredIconType | null
  /**
   * Optional custom header slot — rendered INSTEAD of the default
   * icon + title + count header. Used by ProjectPage / ListPage
   * to inject the ProjectHeader component.
   */
  headerSlot?: React.ReactNode
  /** Primary task items */
  items: ViewItem[]
  /** Optional secondary section (e.g., "Later" in project/list) */
  laterItems?: ViewItem[]
  /** Label for the laterItems section (defaults to "Someday") */
  laterLabel?: string
  /** Empty state when no items */
  emptyState: { title: string; subtitle: string }
  /** Total count override (defaults to items.length) */
  count?: number
  /**
   * Optimistic setter from useSuspendingLiveQuery (via useViewData).
   * Required for DnD reorder — passes through to DndTaskList.
   * When provided + view is sortable, the list gets drag-to-reorder.
   */
  setOptimistic?: React.Dispatch<React.SetStateAction<ViewData | null>>
  /**
   * When true, the view is still loading initial data.
   * Hides EmptyState and applies `animate-pulse pointer-events-none`
   * to the content area so placeholder rows pulse while real data loads.
   */
  isLoading?: boolean
  /**
   * Optional slot rendered between the header and the content area.
   * Used for ExternalTodayCalendar in Today and Upcoming views.
   */
  beforeContent?: React.ReactNode
  /**
   * Logged items state for container views (Project, List, Area).
   * When provided, renders "Visa X loggade" toggle + logged items section.
   */
  loggedState?: ContainerLoggedState
  /**
   * Extra actions for the ViewHeader overflow menu (view-specific).
   */
  actions?: ViewHeaderAction[]
}

// ---------------------------------------------------------------------------
// Section header (for "Later" section in project/list views)
// ---------------------------------------------------------------------------

function SectionHeader({
  label,
  count,
  collapsed,
  onToggle,
}: {
  label: string
  count: number
  collapsed?: boolean
  onToggle?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 border-t border-border px-4 pb-1 pt-4 text-left"
    >
      {onToggle && (
        <ChevronRightIcon
          width={12}
          height={12}
          className={cn(
            "shrink-0 text-muted-foreground/60 transition-transform duration-150",
            collapsed ? "rotate-0" : "rotate-90",
          )}
        />
      )}
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-xs tabular-nums text-muted-foreground/60">
        {count}
      </span>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Someday section — collapsible section for laterItems
// ---------------------------------------------------------------------------

function SomedaySection({
  items,
  label,
  activeTaskId,
  isEditingTitle,
  openTask,
  closeTask,
}: {
  items: ViewItem[]
  label?: string
  activeTaskId: string | null
  isEditingTitle: boolean
  openTask: (id: string, editTitle?: boolean) => void
  closeTask: () => void
}) {
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = useState(false)

  return (
    <>
      <SectionHeader
        label={label ?? t("Someday")}
        count={items.length}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
      />
      {!collapsed && (
        <SimpleList
          items={items}
          activeTaskId={activeTaskId}
          isEditingTitle={isEditingTitle}
          openTask={openTask}
          closeTask={closeTask}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Virtualized task list
// ---------------------------------------------------------------------------

const ROW_HEIGHT = 44
const OVERSCAN = 15

/** Single virtualized row — memo'd to avoid re-renders */
const VirtualRow = memo(function VirtualRow({
  item,
  onComplete,
  onUncomplete,
  isActive,
  onOpen,
  onClose,
  editTitle,
  isFirstInGroup,
  isLastInGroup,
  isNew,
}: {
  item: ViewItem
  onComplete: (id: string) => void
  onUncomplete: (id: string) => void
  isActive: boolean
  onOpen: (id: string) => void
  onClose: () => void
  editTitle: boolean
  isFirstInGroup?: boolean
  isLastInGroup?: boolean
  isNew?: boolean
}) {
  return (
    <TaskItem
      task={item.task}
      onComplete={onComplete}
      onUncomplete={onUncomplete}
      isActive={isActive}
      startAtData={item.startAtData}
      editTitle={editTitle}
      onOpen={onOpen}
      onClose={onClose}
      isFirstInGroup={isFirstInGroup}
      isLastInGroup={isLastInGroup}
      isNew={isNew}
    />
  )
})

function VirtualizedList({
  items,
  activeTaskId,
  isEditingTitle,
  openTask,
  closeTask,
  newTaskIds,
}: {
  items: ViewItem[]
  activeTaskId: string | null
  isEditingTitle: boolean
  openTask: (id: string) => void
  closeTask: () => void
  newTaskIds?: Set<string>
}) {
  const { scrollRef } = useMainScroll()
  const listRef = useRef<HTMLDivElement>(null)
  const { completeTask, uncompleteTask } = useTaskActions()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { firsts, lasts } = useMemo(() => computeGroupPositions(items), [items])

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      // Expanded rows need more height
      const item = items[index]
      if (item && item.task.id === activeTaskId) return 220
      return ROW_HEIGHT
    },
    overscan: OVERSCAN,
    scrollMargin: listRef.current?.offsetTop ?? 0,
  })

  // When the active task changes, tell the virtualizer to re-measure all items
  // so the expanded/collapsed row sizes update without scroll jumps.
  const prevActiveRef = useRef(activeTaskId)
  useEffect(() => {
    if (prevActiveRef.current !== activeTaskId) {
      prevActiveRef.current = activeTaskId
      virtualizer.measure()
    }
  }, [activeTaskId, virtualizer])

  const handleComplete = useCallback(
    (id: string) => completeTask(id),
    [completeTask],
  )
  const handleUncomplete = useCallback(
    (id: string) => uncompleteTask(id),
    [uncompleteTask],
  )

  return (
    <div ref={listRef}>
      <div
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index]
          if (!item) return null
          const taskId = item.task.id ?? ""
          const isActive = taskId === activeTaskId
          return (
            <div
              key={virtualRow.key}
              className="absolute left-0 w-full"
              style={{
                height: isActive ? undefined : `${virtualRow.size}px`,
                top: virtualRow.start - virtualizer.options.scrollMargin,
                zIndex: isActive ? 50 : undefined,
              }}
            >
              <VirtualRow
                item={item}
                onComplete={handleComplete}
                onUncomplete={handleUncomplete}
                isActive={isActive}
                onOpen={openTask}
                onClose={closeTask}
                editTitle={isActive && isEditingTitle}
                isFirstInGroup={firsts[virtualRow.index]}
                isLastInGroup={lasts[virtualRow.index]}
                isNew={newTaskIds?.has(item.task.id as string)}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Non-virtualized list (for small lists / container "later" section)
// ---------------------------------------------------------------------------

function SimpleList({
  items,
  activeTaskId,
  isEditingTitle,
  openTask,
  closeTask,
  newTaskIds,
}: {
  items: ViewItem[]
  activeTaskId: string | null
  isEditingTitle: boolean
  openTask: (id: string) => void
  closeTask: () => void
  newTaskIds?: Set<string>
}) {
  const { completeTask, uncompleteTask } = useTaskActions()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { firsts, lasts } = useMemo(() => computeGroupPositions(items), [items])

  return (
    <div className="flex flex-col">
      {items.map((item, idx) => {
        const taskId = item.task.id ?? ""
        const isActive = taskId === activeTaskId
        return (
          <TaskItem
            key={taskId}
            task={item.task}
            onComplete={completeTask}
            onUncomplete={uncompleteTask}
            isActive={isActive}
            startAtData={item.startAtData}
            editTitle={isActive && isEditingTitle}
            onOpen={openTask}
            onClose={closeTask}
            isFirstInGroup={firsts[idx]}
            isLastInGroup={lasts[idx]}
            isNew={newTaskIds?.has(taskId)}
          />
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ViewShell
// ---------------------------------------------------------------------------

/** Threshold: use virtualizer for lists above this size */
const VIRTUALIZE_THRESHOLD = 50

/**
 * Shared view wrapper — renders the header, virtualized task list,
 * optional "later" section, and empty state.
 *
 * Used by all view pages (Today, Inbox, Project, etc.).
 */
export function ViewShell({
  title,
  icon,
  headerSlot,
  items,
  laterItems = [],
  laterLabel,
  emptyState,
  count,
  setOptimistic,
  isLoading = false,
  beforeContent,
  loggedState,
  actions,
}: ViewShellProps) {
  const isSortable = useViewContext((s) => s.isSortable)
  const indexField = useViewContext((s) => s.indexField)
  const viewId = useViewContext((s) => s.viewId)
  const containerId = useViewContext((s) => s.containerId)
  const pinnedIds = usePinnedIds(containerId)
  const { t } = useTranslation()
  const { completeTask, uncompleteTask } = useTaskActions()
  const { activeTaskId, isEditingTitle, openTask, closeTask } = useActiveTask()
  const { scrollRef } = useMainScroll()

  // ── New-task tracking ─────────────────────────────────────────────────
  const { lastSeenAt, viewOpenedAt } = useViewSeen(viewId, containerId)
  const [newBadgesDismissed, setNewBadgesDismissed] = useState(false)
  // Reset dismissed badges when navigating to a different view
  useEffect(() => { setNewBadgesDismissed(false) }, [viewId, containerId])

  // Compute new task IDs for per-row blue dot badges
  const newTaskIds = useMemo(() => {
    if (newBadgesDismissed || !lastSeenAt) return new Set<string>()
    const ids = new Set<string>()
    for (const vi of items) {
      if (vi.task.id && isNewTask(vi.task, lastSeenAt, viewOpenedAt)) {
        ids.add(vi.task.id)
      }
    }
    return ids
  }, [items, lastSeenAt, viewOpenedAt, newBadgesDismissed])

  // ── Tag filtering ─────────────────────────────────────────────────────
  const showTagFilter = viewShowsTagFilter(viewId)
  const selectedTags = useTagFilterStore((s) => s.selectedTags)
  const setAvailableTags = useTagFilterStore((s) => s.setAvailableTags)
  const filterText = useTagFilterStore((s) => s.filterText)

  // Subscribe to ancestor tag store version (re-render only on actual changes)
  const ancestorVersion = useAncestorTagStore((s) => s.version)

  // Enrich items with inherited tags (own tags + ancestor tags)
  const enrichedItems = useMemo(() => {
    if (!showTagFilter) return items as (ViewItem & { allTags: string[] })[]
    const state = useAncestorTagStore.getState()
    return items.map((vi) => {
      const inherited = getInheritedTags(vi.task.parentPath, state)
      const own = vi.task.tags ?? []
      const allTags =
        inherited.length === 0
          ? own
          : Array.from(new Set([...own, ...inherited])).sort((a, b) =>
              a.localeCompare(b),
            )
      return { ...vi, allTags }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTagFilter, items, ancestorVersion])

  const enrichedLaterItems = useMemo(() => {
    if (!showTagFilter)
      return laterItems as (ViewItem & { allTags: string[] })[]
    const state = useAncestorTagStore.getState()
    return laterItems.map((vi) => {
      const inherited = getInheritedTags(vi.task.parentPath, state)
      const own = vi.task.tags ?? []
      const allTags =
        inherited.length === 0
          ? own
          : Array.from(new Set([...own, ...inherited])).sort((a, b) =>
              a.localeCompare(b),
            )
      return { ...vi, allTags }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTagFilter, laterItems, ancestorVersion])

  // Compute available tags from unfiltered enriched items
  const allEnriched = useMemo(
    () => [...enrichedItems, ...enrichedLaterItems],
    [enrichedItems, enrichedLaterItems],
  )
  const computedTags = useMemo(
    () => (showTagFilter ? getUniqueTagsFromItems(allEnriched) : []),
    [showTagFilter, allEnriched],
  )

  // Use cached viewTags while loading (placeholder data only has a subset of
  // items, so computedTags from it is incomplete). Once real data arrives,
  // switch to the fully computed enriched tags.
  const cachedTags = useMemo(
    () => (showTagFilter ? getCachedViewTags(viewId) : []),
    [showTagFilter, viewId],
  )
  const availableTags = useMemo(
    () =>
      isLoading && cachedTags.length > 0
        ? cachedTags
        : computedTags.length > 0
          ? computedTags
          : cachedTags,
    [isLoading, cachedTags, computedTags],
  )

  // Push available tags to store (debounced by React's commit phase)
  useEffect(() => {
    if (showTagFilter) {
      setAvailableTags(availableTags)
    }
  }, [showTagFilter, availableTags, setAvailableTags])

  // Cache enriched tags (own + inherited) so next load shows all tags immediately
  useEffect(() => {
    if (!isLoading && computedTags.length > 0) {
      cacheEnrichedViewTags(viewId, computedTags)
    }
  }, [isLoading, computedTags, viewId])

  // Apply tag filter to enriched items
  const tagFilteredItems = useMemo(
    () =>
      showTagFilter && selectedTags.length > 0
        ? filterBySelectedTags(enrichedItems, selectedTags)
        : enrichedItems,
    [showTagFilter, selectedTags, enrichedItems],
  )
  const tagFilteredLaterItems = useMemo(
    () =>
      showTagFilter && selectedTags.length > 0
        ? filterBySelectedTags(enrichedLaterItems, selectedTags)
        : enrichedLaterItems,
    [showTagFilter, selectedTags, enrichedLaterItems],
  )

  // Apply free-text filter on top of tag filter
  const textFilteredItems = useMemo(
    () =>
      filterText
        ? filterByText(tagFilteredItems, filterText)
        : tagFilteredItems,
    [filterText, tagFilteredItems],
  )
  const textFilteredLaterItems = useMemo(
    () =>
      filterText
        ? filterByText(tagFilteredLaterItems, filterText)
        : tagFilteredLaterItems,
    [filterText, tagFilteredLaterItems],
  )

  // Apply view filters (status, deadline, sort) on top of tag + text filters
  const viewFilterCriteria = useViewFilterStore(
    useShallow(
      (s): ViewFilterCriteria => ({
        // Logbook/Trash always show completed+canceled (that's their purpose)
        showCompleted:
          viewId === "logbook" || viewId === "trash" ? true : s.showCompleted,
        showCanceled:
          viewId === "logbook" || viewId === "trash" ? true : s.showCanceled,
        hasDueDate: s.hasDueDate,
        overdueOnly: s.overdueOnly,
        userStatuses: s.userStatuses,
        areaId: s.areaId,
        sort: s.sort,
      }),
    ),
  )
  const viewFilterActive = useViewFilterStore((s) => s.isFiltering())
  const projectToAreaMap = useAncestorTagStore((s) => s.projectToAreaMap)
  const filteredItems = useMemo(() => {
    const filtered = applyViewFilters(textFilteredItems, viewFilterCriteria, projectToAreaMap)
    return applySortOverride(filtered, viewFilterCriteria.sort)
  }, [textFilteredItems, viewFilterCriteria, projectToAreaMap])

  // Remove pinned tasks from the flow — they appear in PinnedSection instead
  const unpinnedItems = useMemo(() => {
    if (pinnedIds.size === 0) return filteredItems
    return filteredItems.filter((vi) => !pinnedIds.has(vi.task.id ?? ""))
  }, [filteredItems, pinnedIds])

  const filteredLaterItems = useMemo(() => {
    const filtered = applyViewFilters(
      textFilteredLaterItems,
      viewFilterCriteria,
      projectToAreaMap,
    )
    return applySortOverride(filtered, viewFilterCriteria.sort)
  }, [textFilteredLaterItems, viewFilterCriteria, projectToAreaMap])

  // ── Derived state ─────────────────────────────────────────────────────
  const totalCount = count ?? filteredItems.length + filteredLaterItems.length
  const isEmpty = filteredItems.length === 0 && filteredLaterItems.length === 0

  // ── Sync page actions + count to viewMenuStore (for TopNavMenu) ────────
  useEffect(() => {
    useViewMenuStore.getState().setPageActions(actions ?? [])
    useViewMenuStore.getState().setTotalCount(totalCount)
    return () => {
      useViewMenuStore.getState().setPageActions([])
      useViewMenuStore.getState().setTotalCount(0)
    }
  }, [actions, totalCount])

  // ── Keyboard shortcut: toggle logged items (Mod+Shift+E) ───────────
  useEffect(() => {
    if (!loggedState) return
    const handler = () => loggedState.setShowLogged(!loggedState.showLogged)
    document.addEventListener("totodo:toggle-logged", handler)
    return () => document.removeEventListener("totodo:toggle-logged", handler)
  }, [loggedState])

  const firstItem = filteredItems[0]
  const { value: isFlatToday } = useSettingBoolean("#flat-today")

  const isGroupedView =
    viewShowsGrouping(viewId) && !(viewId === "today" && isFlatToday)
  const dateGroupMode = getDateGroupMode(viewId)

  // Only Area pages should render via AreaList. Previously this checked
  // firstItem.type which broke Today/Anyday when a Project or List happened
  // to be the first item — all tasks then rendered as plain text without
  // checkboxes. Now we explicitly check viewId === "area".
  const isAreaView =
    viewId === "area" &&
    !dateGroupMode &&
    filteredItems.length > 0 &&
    firstItem != null &&
    firstItem.task.type !== ObjectType.Task &&
    firstItem.task.type !== ObjectType.Section

  // ── Dynamic document title ────────────────────────────────────────────
  // Show global badge (inbox + invites) as count prefix on all views,
  // so the user notices incoming items even from other views.
  // Blink the tab whenever there are attention items and the tab is hidden.
  const globalBadge = useGlobalBadgeCount()
  const titleCount =
    viewId === "inbox"
      ? totalCount > 0
        ? totalCount
        : undefined
      : globalBadge > 0
        ? globalBadge
        : totalCount > 0
          ? totalCount
          : undefined

  useDocumentTitle({
    title,
    count: titleCount,
    blinkOnHidden: globalBadge > 0,
    blinkMessage: globalBadge > 0 ? `📬 ${globalBadge} new` : undefined,
  })

  // ── Push visible task IDs for Shift+Click range selection ─────────────
  // In grouped mode, GroupedTaskList handles this (with group boundary sentinels).
  // Only set here for flat (non-grouped) views to avoid overwriting grouped order.
  const setVisibleTaskIds = useViewContext((s) => s.setVisibleTaskIds)
  const setVisibleSectionIds = useViewContext((s) => s.setVisibleSectionIds)
  const setGroupedAllTaskIds = useViewContext((s) => s.setGroupedAllTaskIds)
  const clearSelection = useViewContext((s) => s.clearSelection)
  const hasSelection = useViewContext((s) => s.selectedTaskIds.length > 0)
  const allVisibleItems = useMemo(
    () => [...filteredItems, ...filteredLaterItems],
    [filteredItems, filteredLaterItems],
  )

  // ── Auto-reveal logged section on deep link ───────────────────────────
  // When ?tid= points to a completed/canceled task in a container view,
  // auto-expand the logged section so the task detail can open.
  useEffect(() => {
    if (!activeTaskId || !loggedState || loggedState.showLogged) return
    // Task already visible in the main items → no need to reveal logged
    if (allVisibleItems.some((vi) => vi.task.id === activeTaskId)) return

    // Check if the task is completed/canceled in this container
    void db.totodo.get(activeTaskId).then((task) => {
      if (
        task &&
        task.isDeleted !== 1 &&
        (task.status === ObjectStatus.Completed ||
          task.status === ObjectStatus.Canceled)
      ) {
        loggedState.setShowLogged(true)
      }
    })
  }, [activeTaskId, loggedState, allVisibleItems])

  // ── Stale task detection ──────────────────────────────────────────────
  // Auto-close task detail when the open task disappears from the current
  // view (deleted, completed, moved by another user/API, filtered out).
  // Include logged items in the scope so revealed logged tasks aren't closed.
  const staleDetectionItems = useMemo(
    () =>
      loggedState?.showLogged && loggedState.loggedItems.length > 0
        ? [...allVisibleItems, ...loggedState.loggedItems]
        : allVisibleItems,
    [allVisibleItems, loggedState?.showLogged, loggedState?.loggedItems],
  )
  useStaleTaskDetection(activeTaskId, staleDetectionItems, closeTask, viewId)

  useEffect(() => {
    if (isGroupedView) return // GroupedTaskList manages visibleTaskIds
    if (dateGroupMode) return // DateGroupedList manages visibleTaskIds
    const ids = allVisibleItems
      .filter(
        (vi) =>
          vi.task.type === ObjectType.Task ||
          vi.task.type === ObjectType.Section,
      )
      .map((vi) => vi.task.id ?? "")
      .filter(Boolean)
    setVisibleTaskIds(ids)
    // Flat views: single group containing all tasks (no "Show more" to hide behind)
    setGroupedAllTaskIds([ids])

    const sectionIds = new Set<string>()
    for (const vi of allVisibleItems) {
      if (vi.task.type === ObjectType.Section && vi.task.id) {
        sectionIds.add(vi.task.id)
      }
    }
    setVisibleSectionIds(sectionIds)
  }, [
    allVisibleItems,
    setVisibleTaskIds,
    setVisibleSectionIds,
    setGroupedAllTaskIds,
    isGroupedView,
    dateGroupMode,
  ])

  // Adapter: DndTaskList works with ViewItem[] but setOptimistic expects ViewData.
  // This callback wraps/unwraps the ViewData envelope (preserving laterItems).
  //
  // When a filter is active, DndTaskList only sees the *filtered* subset.
  // A reorder call with a direct array would replace ALL items with only the
  // filtered ones, losing the non-matching items from the optimistic state.
  // To fix this, we merge the reordered subset back into the full list:
  // non-matching items are kept at their original positions, matching items
  // are replaced by the reordered array.
  //
  // IMPORTANT: setOptimistic is backed by React.useState<ViewData | null>(null).
  // The `prev` value in setOptimistic((prev) => ...) is null until the first
  // optimistic update. We must NOT rely on prev for the full item list.
  // Instead, we keep refs to the current `items` and `laterItems` props.
  const isFilterActive =
    (showTagFilter && selectedTags.length > 0) ||
    !!filterText ||
    viewFilterActive

  // Refs for the full (unfiltered) item lists — always up-to-date.
  const itemsRef = useRef(items)
  itemsRef.current = items
  const laterItemsRef = useRef(laterItems)
  laterItemsRef.current = laterItems

  const filteredIdSetRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    filteredIdSetRef.current = new Set(
      filteredItems.map((vi) => vi.task.id ?? ""),
    )
  }, [filteredItems])

  const handleDndSetOptimistic = useCallback(
    (
      updater:
        | ViewItem[]
        | ((prev: ViewItem[] | null) => ViewItem[] | null)
        | null,
    ) => {
      if (!setOptimistic) return
      if (updater === null) {
        setOptimistic(null) // rollback
        return
      }
      if (typeof updater === "function") {
        setOptimistic((prev) => {
          // Use prev.items if we have a prior optimistic state, otherwise
          // fall back to the current full item list from props.
          const currentItems = prev?.items ?? itemsRef.current
          const newItems = updater(currentItems)
          if (!newItems) return null
          return {
            items: newItems,
            laterItems: prev?.laterItems ?? laterItemsRef.current,
          }
        })
        return
      }
      // Direct array — merge reordered filtered items back into full list
      if (isFilterActive) {
        setOptimistic((prev) => {
          // Use previous optimistic items if available, otherwise the full
          // prop list. This ensures non-matching items are never lost.
          const fullItems = prev?.items ?? itemsRef.current
          const filteredIds = filteredIdSetRef.current
          // Keep items that were NOT part of the filtered set
          const nonMatching = fullItems.filter(
            (vi) => !filteredIds.has(vi.task.id ?? ""),
          )
          // DnD already computed correct sortIndexes for the reordered items.
          // Merge non-matching + reordered and sort by sortIndex so the full
          // list stays in correct order when the filter is later cleared.
          const finalItems = [...nonMatching, ...updater].sort(
            (a, b) => a.sortIndex - b.sortIndex,
          )
          return {
            items: finalItems,
            laterItems: prev?.laterItems ?? laterItemsRef.current,
          }
        })
        return
      }
      // No filter active — replace directly
      setOptimistic((prev) => ({
        items: updater,
        laterItems: prev?.laterItems ?? laterItemsRef.current,
      }))
    },
    [setOptimistic, isFilterActive],
  )

  const canDnd = isSortable && setOptimistic != null

  // Show quick-add in views where creating new tasks makes sense
  const showQuickAdd =
    !isAreaView &&
    viewId !== "logbook" &&
    viewId !== "trash" &&
    viewId !== "search" &&
    viewId !== "recurring" &&
    viewId !== "routines"

  // ── Click-outside-to-close ─────────────────────────────────────────────
  // Tap/click outside .task-row-active closes the task detail.
  //
  // Desktop (mouse): close on pointerdown immediately.
  // Mobile (touch): use tap detection — only close if the user taps without
  // scrolling. This lets users scroll the dimmed background without
  // accidentally closing the task detail.
  //
  // We must NOT close the task when a modal dialog/drawer is open (e.g.
  // schedule calendar, tag picker). These render in portals outside the
  // task card DOM.
  //
  // IMPORTANT: We use `pointerdown` in the **capture phase** because
  // Radix Dialog uses `pointerdown` to dismiss. If we used `mousedown`
  // (which fires after `pointerdown`), Radix would already have unmounted
  // the dialog from the DOM by the time our handler runs. In capture phase,
  // our handler fires first while the dialog is still present.
  useEffect(() => {
    if (!activeTaskId) return

    // Touch tap-detection state
    let touchStartX = 0
    let touchStartY = 0
    let touchMoved = false
    let touchPending = false
    const TAP_THRESHOLD = 10 // px — distinguish tap from scroll

    function isOutsideActiveTask(target: Element): boolean {
      const activeCard = document.querySelector(".task-row-active")
      if (activeCard?.contains(target)) return false

      // Don't close when clicking ActionPanel, the portaled editor toolbar,
      // the floating keyboard-dismiss / done button, portaled menus
      // (e.g. ChecklistStatusMenu rendered via createPortal to body),
      // the AI chat panel, edge pills, or resize handles.
      //
      // [data-context-menu-backdrop] matters as much as [role='menu']: the
      // menu panel was excluded but its full-screen backdrop was not, so
      // tapping outside the menu to dismiss it counted as "outside the active
      // task" and closed the whole task. This listener is on document in the
      // capture phase, so it runs before the backdrop's own handler and
      // stopPropagation there cannot help.
      if (target.closest("[data-action-panel], #editor-toolbar-slot, [data-floating-button], [role='menu'], [data-context-menu-backdrop], [data-ai-chat], [data-panel-pill], [data-slot='resizable-handle'], [data-timer-bar]")) return false

      // Don't close if a modal dialog/drawer is currently open
      if (
        document.querySelector(
          '[role="dialog"], [data-vaul-drawer], [data-radix-portal]',
        )
      )
        return false

      return true
    }

    const downHandler = (e: PointerEvent) => {
      const target = e.target as Element | null
      if (!target) return
      if (!isOutsideActiveTask(target)) return

      // Mouse / pen: close immediately (desktop behavior)
      if (e.pointerType === "mouse" || e.pointerType === "pen") {
        closeTask()
        return
      }

      // Touch: start tap detection
      touchStartX = e.clientX
      touchStartY = e.clientY
      touchMoved = false
      touchPending = true
    }

    const moveHandler = (e: PointerEvent) => {
      if (!touchPending || e.pointerType === "mouse") return
      const dx = Math.abs(e.clientX - touchStartX)
      const dy = Math.abs(e.clientY - touchStartY)
      if (dx > TAP_THRESHOLD || dy > TAP_THRESHOLD) {
        touchMoved = true
      }
    }

    const upHandler = (e: PointerEvent) => {
      if (!touchPending || e.pointerType === "mouse") return
      if (!touchMoved) {
        closeTask()
      }
      touchPending = false
    }

    const cancelHandler = () => {
      touchPending = false
    }

    document.addEventListener("pointerdown", downHandler, true)
    document.addEventListener("pointermove", moveHandler, true)
    document.addEventListener("pointerup", upHandler, true)
    document.addEventListener("pointercancel", cancelHandler, true)
    return () => {
      document.removeEventListener("pointerdown", downHandler, true)
      document.removeEventListener("pointermove", moveHandler, true)
      document.removeEventListener("pointerup", upHandler, true)
      document.removeEventListener("pointercancel", cancelHandler, true)
    }
  }, [activeTaskId, closeTask])

  // ── Scroll clamp when task is open (desktop only) ───────────────────────
  // Prevent scrolling past the open task's edges on desktop. The user can
  // scroll through the task's full height but can't scroll it out of view.
  // On mobile the task detail fills the screen, so clamping would fight
  // the user's natural scroll.
  const isDesktop = useBreakpoint().isDesktop
  useEffect(() => {
    if (!activeTaskId || !isDesktop) return
    const container = scrollRef.current
    if (!container) return

    const HEADER_HEIGHT = 72 // header + padding (matches scroll-margin-top)

    // Cache element dimensions to avoid layout thrashing on every scroll event.
    // ResizeObserver updates them when the task card grows/shrinks (e.g. checklist loads).
    let elTop = 0
    let elHeight = 0
    let valid = false

    const measure = () => {
      const el = container.querySelector(".task-row-active") as HTMLElement | null
      if (!el) { valid = false; return }
      // Use getBoundingClientRect + scrollTop to get the element's true
      // position within the scroll container. el.offsetTop is relative to the
      // nearest positioned ancestor (.task-list-dimmed has position:relative)
      // which does NOT account for content above it (header, calendar, etc.),
      // causing the clamp to scroll to the wrong position.
      const elRect = el.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      elTop = elRect.top - containerRect.top + container.scrollTop
      elHeight = elRect.height
      valid = true
    }

    // Delay attaching the clamp listener until the open animation has settled.
    // The task-height-in animation runs for ~500ms. If we clamp immediately,
    // elHeight is still ~44px (compact row) and the clamp fights the
    // enhancedSmoothScroll animation, jumping the view to the wrong position.
    const SETTLE_DELAY = 600 // ms — slightly longer than task-height-in

    const ro = new ResizeObserver(measure)
    let clampAttached = false

    const clamp = () => {
      if (!valid) return
      const viewH = container.clientHeight

      // Guarantee at least MIN_VISIBLE px of the task card stays within the
      // viewport at all times. This lets the user scroll quite a bit above
      // and below the open task while preventing it from disappearing.
      //
      // Visible area: [scrollTop + HEADER_HEIGHT, scrollTop + viewH]
      // Task area:    [elTop, elTop + elHeight]
      //
      // Constraint 1 (scrolled too far up → task below viewport):
      //   At least MIN_VISIBLE of task top is above viewport bottom
      //   → scrollTop >= elTop - viewH + MIN_VISIBLE
      //
      // Constraint 2 (scrolled too far down → task above viewport):
      //   At least MIN_VISIBLE of task bottom is below viewport top + header
      //   → scrollTop <= elTop + elHeight - HEADER_HEIGHT - MIN_VISIBLE
      const MIN_VISIBLE = 60 // px of the task that must remain visible
      const minScroll = Math.max(0, elTop - viewH + MIN_VISIBLE)
      const maxScroll = elTop + elHeight - HEADER_HEIGHT - MIN_VISIBLE

      if (maxScroll <= minScroll) return // shouldn't happen, but safety
      if (container.scrollTop < minScroll) container.scrollTop = minScroll
      else if (container.scrollTop > maxScroll) container.scrollTop = maxScroll
    }

    const settleTimer = setTimeout(() => {
      // Measure after animation has settled for accurate dimensions
      measure()

      const el = container.querySelector(".task-row-active")
      if (el) ro.observe(el)

      container.addEventListener("scroll", clamp, { passive: true })
      clampAttached = true
    }, SETTLE_DELAY)

    return () => {
      clearTimeout(settleTimer)
      if (clampAttached) {
        container.removeEventListener("scroll", clamp)
      }
      ro.disconnect()
    }
  }, [activeTaskId, isDesktop, scrollRef])

  // ── Click-outside-to-deselect (§18) ────────────────────────────────────
  // Click on empty space (not on a task row) clears the selection.
  // On touch devices, only clear on TAP (not scroll) — same approach as
  // the task-close handler above.
  useEffect(() => {
    if (!hasSelection) return

    // Touch tap-detection state
    let touchStartX = 0
    let touchStartY = 0
    let touchMoved = false
    let touchPending = false
    const TAP_THRESHOLD = 10 // px

    function shouldIgnore(target: Element): boolean {
      // Don't deselect if clicking inside a task row
      if (target.closest("[data-task-row]")) return true

      // Don't deselect if clicking inside a context menu or its backdrop
      if (target.closest('[role="menu"]') || target.closest('[data-context-menu-backdrop]')) return true

      // Don't deselect if a modal dialog/drawer/dropdown is open
      if (
        document.querySelector(
          '[role="dialog"], [data-vaul-drawer], [data-radix-portal]',
        )
      )
        return true

      // Don't deselect if clicking sidebar or header controls
      if (target.closest("[data-sidebar]") || target.closest("[data-header]"))
        return true

      // Don't deselect if clicking the selection toolbar (TopNavBar selection mode)
      if (target.closest("[data-selection-toolbar]")) return true

      // Don't deselect if clicking the quick-add input area
      if (target.closest("[data-quick-add]")) return true

      // Don't deselect if clicking inside the action panel
      if (target.closest("[data-action-panel]")) return true

      // Don't deselect if clicking floating keyboard/done button
      if (target.closest("[data-floating-button]")) return true

      return false
    }

    const downHandler = (e: PointerEvent) => {
      const target = e.target as Element | null
      if (!target) return
      if (shouldIgnore(target)) return

      // Mouse / pen: clear immediately (desktop behavior)
      if (e.pointerType === "mouse" || e.pointerType === "pen") {
        clearSelection()
        return
      }

      // Touch: start tap detection — don't clear on scroll
      touchStartX = e.clientX
      touchStartY = e.clientY
      touchMoved = false
      touchPending = true
    }

    const moveHandler = (e: PointerEvent) => {
      if (!touchPending || e.pointerType === "mouse") return
      const dx = Math.abs(e.clientX - touchStartX)
      const dy = Math.abs(e.clientY - touchStartY)
      if (dx > TAP_THRESHOLD || dy > TAP_THRESHOLD) {
        touchMoved = true
      }
    }

    const upHandler = (e: PointerEvent) => {
      if (!touchPending || e.pointerType === "mouse") return
      if (!touchMoved) {
        clearSelection()
      }
      touchPending = false
    }

    const cancelHandler = () => {
      touchPending = false
    }

    document.addEventListener("pointerdown", downHandler, true)
    document.addEventListener("pointermove", moveHandler, true)
    document.addEventListener("pointerup", upHandler, true)
    document.addEventListener("pointercancel", cancelHandler, true)
    return () => {
      document.removeEventListener("pointerdown", downHandler, true)
      document.removeEventListener("pointermove", moveHandler, true)
      document.removeEventListener("pointerup", upHandler, true)
      document.removeEventListener("pointercancel", cancelHandler, true)
    }
  }, [hasSelection, clearSelection])

  // Key for crossfade animation — changes when navigating between views.
  // Uses pathname (without query) so project→project nav also animates.
  const { pathname } = useLocation()
  const crossfadeKey = pathname

  return (
    <div
      key={crossfadeKey}
      className="view-crossfade relative mx-auto max-w-[900px]"
    >
      {/* Header */}
      {headerSlot ? (
        headerSlot
      ) : (
        <ViewHeader
          title={title}
          icon={icon}
          totalCount={totalCount}
          showTagFilter={showTagFilter}
          actions={actions}
        />
      )}

      {/* Before-content slot (e.g. ExternalTodayCalendar) */}
      {beforeContent}

      {/* New-task banner (shows "N new tasks since [time]") */}
      <NewTasksBanner items={items} lastSeenAt={lastSeenAt} viewOpenedAt={viewOpenedAt} onDismiss={() => setNewBadgesDismissed(true)} />

      {/* Content */}
      {isEmpty &&
      !showQuickAdd &&
      !isLoading &&
      dateGroupMode !== "upcoming" ? (
        <EmptyState
          {...emptyState}
          icon={icon}
          filterTags={
            showTagFilter && selectedTags.length > 0 ? selectedTags : undefined
          }
          cta={
            isFilterActive ? (
              <button
                type="button"
                onClick={() => {
                  useTagFilterStore.getState().reset()
                  useViewFilterStore.getState().reset()
                }}
                className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {t("Clear All Filters")}
              </button>
            ) : undefined
          }
        />
      ) : (
        <div
          className={cn(
            activeTaskId && "task-list-dimmed",
            isLoading && "animate-pulse pointer-events-none",
          )}
        >
          {/* Quick-add input (hidden in upcoming — no manual task creation) */}
          {showQuickAdd && dateGroupMode !== "upcoming" && <QuickAdd />}
          {/* Empty state below QuickAdd */}
          {isEmpty &&
            !isLoading &&
            showQuickAdd &&
            dateGroupMode !== "upcoming" && (
              <EmptyState
                {...emptyState}
                icon={icon}
                filterTags={
                  showTagFilter && selectedTags.length > 0
                    ? selectedTags
                    : undefined
                }
                cta={
                  isFilterActive ? (
                    <button
                      type="button"
                      onClick={() => {
                        useTagFilterStore.getState().reset()
                        useViewFilterStore.getState().reset()
                      }}
                      className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      {t("Clear All Filters")}
                    </button>
                  ) : undefined
                }
              />
            )}
          {/* Primary section */}
          {viewId === "laterProjects" ? (
            <LaterProjectsDndList
              upcomingItems={filteredItems}
              somedayItems={filteredLaterItems}
            />
          ) : (
            (filteredItems.length > 0 || dateGroupMode === "upcoming") &&
            (isAreaView ? (
              <AreaList items={filteredItems} />
            ) : dateGroupMode ? (
              <DateGroupedList items={filteredItems} mode={dateGroupMode} />
            ) : isGroupedView ? (
              <GroupedTaskList
                items={filteredItems}
                dndProps={
                  canDnd
                    ? {
                        indexField,
                        setOptimistic: handleDndSetOptimistic,
                      }
                    : undefined
                }
                maxInitialItems={viewId === "anyday" ? 3 : undefined}
              />
            ) : canDnd ? (
              <>
                {containerId && pinnedIds.size > 0 && (
                  <PinnedSection
                    containerId={containerId}
                    onOpenTask={openTask}
                    activeTaskId={activeTaskId}
                  />
                )}
                <DndTaskList
                  items={unpinnedItems}
                  indexField={indexField}
                  setOptimistic={handleDndSetOptimistic}
                  onComplete={completeTask}
                  onUncomplete={uncompleteTask}
                  activeTaskId={activeTaskId}
                  isEditingTitle={isEditingTitle}
                  onOpenTask={openTask}
                  onCloseTask={closeTask}
                  skipGhostIndex={isFlatToday}
                />
              </>
            ) : filteredItems.length > VIRTUALIZE_THRESHOLD ? (
              <VirtualizedList
                items={filteredItems}
                activeTaskId={activeTaskId}
                isEditingTitle={isEditingTitle}
                openTask={openTask}
                closeTask={closeTask}
                newTaskIds={newTaskIds}
              />
            ) : (
              <SimpleList
                items={filteredItems}
                activeTaskId={activeTaskId}
                isEditingTitle={isEditingTitle}
                openTask={openTask}
                closeTask={closeTask}
                newTaskIds={newTaskIds}
              />
            ))
          )}
          {/* Later / Someday section (project/list views) — collapsible */}
          {viewId !== "laterProjects" && filteredLaterItems.length > 0 && (
            <SomedaySection
              items={filteredLaterItems}
              label={laterLabel}
              activeTaskId={activeTaskId}
              isEditingTitle={isEditingTitle}
              openTask={openTask}
              closeTask={closeTask}
            />
          )}

          {/* Logged items toggle + list rendered OUTSIDE the content div below */}
        </div>
      )}

      {/* Logged items section (container views: Project, List, Area).
          Rendered outside the isEmpty ternary so it shows even when
          all active tasks are completed. Per master plan §11. */}
      {loggedState && loggedState.loggedCount > 0 && (
        <>
          <LoggedItemsToggle
            count={loggedState.loggedCount}
            showLogged={loggedState.showLogged}
            onToggle={loggedState.setShowLogged}
          />
          {loggedState.showLogged &&
            loggedState.loggedItems.length > 0 && (
              <div className="mb-5">
                <SimpleList
                  items={loggedState.loggedItems}
                  activeTaskId={activeTaskId}
                  isEditingTitle={isEditingTitle}
                  openTask={openTask}
                  closeTask={closeTask}
                />
                {loggedState.hasMore && (
                  <button
                    type="button"
                    onClick={loggedState.loadAll}
                    className="mt-2.5 flex cursor-pointer items-center gap-1.5 px-2.5 text-primary"
                  >
                    <span className="text-[15px] font-semibold">
                      {t("Show more...")}
                    </span>
                  </button>
                )}
              </div>
            )}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Area item list (shows projects/lists, not tasks)
// ---------------------------------------------------------------------------

function AreaList({ items }: { items: ViewItem[] }) {
  return (
    <div className="flex flex-col gap-0.5">
      {items.map((item) => (
        <div
          key={item.task.id}
          className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-accent/50 transition-colors"
        >
          <div className="flex size-5 items-center justify-center">
            {item.task.type === ObjectType.Project && (
              <div className="size-3 rounded-full bg-primary/60" />
            )}
            {item.task.type === ObjectType.List && (
              <div className="size-3 rounded-sm bg-muted-foreground/40" />
            )}
          </div>
          <span className="flex-1 text-sm">{item.task.title}</span>
        </div>
      ))}
    </div>
  )
}
